import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
  GetArticlesByCategoryPaginationDocument,
  GetCategoriesWithLatestArticlesDocument,
  Locale,
  type Category,
  type GetArticlesByCategoryPaginationQuery,
  type GetCategoriesWithLatestArticlesQuery,
} from '@seek/cmsu-cms-connect';
import { filterObjectsByContentLocaleOrDefault } from '@seek/cmsu-cms-connect/src/helpers/localizationHelper';
import type { LoaderFunction } from 'react-router';

import {
  getRecordsToSkip,
  pageConfig,
} from '../client/shared/utils/Pagination';
import { getHygraphLocales } from '../client/shared/utils/helper';
import {
  getPreviewProps,
  getRouteInfo,
  getSectionQuery,
  type CategoryLocalization,
  type PreviewProps,
} from '../helpers/loadersHelper';
import { internalError, notFound } from '../responses';
import type { RouteProps } from '../routes';
import { json } from '../server/utils/json';

import { getAboutSubForm_ContactUs } from './forms';

type CategoryQueryFilter = {
  client: ApolloClient<NormalizedCacheObject>;
  slug: string | undefined;
  siteName: string;
  locale: Locale;
  sections: string[];
  pageNumber?: string | undefined;
};

export const categoryLoader =
  ({ config, client }: RouteProps): LoaderFunction =>
  async ({ params }) => {
    // Extract variables
    const { hygraphSite, hygraphLocale, routeArray, isDraft, section } = config;
    const previewProps = getPreviewProps(isDraft);
    const { sections } = getRouteInfo(routeArray, 'category');

    const { slug, pageNumber } = params;

    sections.reverse();
    const category = await getCategoryWithLatestArticlesForChildCategoies(
      {
        client,
        slug,
        siteName: hygraphSite,
        locale: hygraphLocale,
        sections,
      },
      previewProps,
    );

    const articles = await getArticlesByCategory(
      {
        client,
        slug,
        siteName: hygraphSite,
        locale: hygraphLocale,
        sections,
        pageNumber,
      },
      previewProps,
    );

    // If the article is under about section, eg: about/news, load the data for subscription form and contact us

    const aboutSubFormContactUs = await getAboutSubForm_ContactUs(
      section,
      {
        hygraphSite,
        hygraphLocale,
        client,
      },
      previewProps,
    );

    // Return response
    if (
      !category ||
      (category.children.length === 0 &&
        articles.articlesConnection.edges.length === 0)
    ) {
      throw notFound(slug);
    }

    return json({
      category,
      articles: articles.articlesConnection,
      ...aboutSubFormContactUs,
    } as CategoryLocalization);
  };

const getCategoryWithLatestArticlesForChildCategoies = async (
  { client, slug, siteName, locale, sections }: CategoryQueryFilter,
  previewProps: PreviewProps,
): Promise<Category | undefined> => {
  const { stage, queryOptions } = previewProps;

  const relatedCategorySection = getSectionQuery(sections);
  // Query hygraph
  const variables = {
    where: {
      slug,
      sites_some: { name: siteName },
      relatedCategorySection,
    },
    site: siteName,
    sectionWhere: relatedCategorySection,
    locale: getHygraphLocales(locale),
    localesWithDefault: [locale, Locale.Default],
    stage,
  };

  const { data, error } =
    await client.query<GetCategoriesWithLatestArticlesQuery>({
      query: GetCategoriesWithLatestArticlesDocument,
      variables,
      ...queryOptions,
    });

  if (error) {
    throw internalError(error);
  }

  if (data.categories.length > 0 && data.categories[0].localizations.length > 0)
    return (
      filterObjectsByContentLocaleOrDefault(
        data.categories[0].localizations,
        locale,
      ) as Category[]
    )[0];
  return;
};

const getArticlesByCategory = async (
  { client, slug, siteName, locale, sections, pageNumber }: CategoryQueryFilter,
  previewProps: PreviewProps,
) => {
  const { stage, queryOptions } = previewProps;

  const skip = getRecordsToSkip(pageNumber);
  // Query hygraph
  const variables = {
    where: {
      title_not: '-',
      category: {
        slug,
        sites_some: { name: siteName },
        relatedCategorySection: getSectionQuery(sections),
      },
      sites_some: { name: siteName },
    },
    locale: getHygraphLocales(locale),
    localesWithDefault: [locale, Locale.Default],
    stage,
    first: pageConfig.pageSize,
    skip,
  };

  const { data, error } =
    await client.query<GetArticlesByCategoryPaginationQuery>({
      query: GetArticlesByCategoryPaginationDocument,
      variables,
      ...queryOptions,
    });

  if (error) {
    throw internalError(error);
  }

  return data;
};
